import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { auSignalDeMaintenance, maintenanceObservee } from '@/lib/api/client'
import { etatDeLaMaintenance } from '@/lib/api/maintenance'
import { MINUTES_D_UNE_EPREUVE } from './heures'

/**
 * L'état de la maintenance, tel que les écrans le lisent — [DECISIONS §196].
 *
 * **Deux sources, et il en faut deux.**
 *
 * 1. `GET /api/v1/maintenance`, interrogée en boucle lente. C'est elle qui
 *    porte l'ANNONCE — la seule chose qu'on connaisse avant la coupure.
 * 2. Le 503 aperçu par `client.ts`. Il arrive plus tôt que le sondage suivant,
 *    et surtout il arrive quand `php artisan down` a coupé la pile pendant que
 *    le sondage, lui, échouait. Sans lui, le candidat verrait ses écrans se
 *    taire pendant vingt secondes sans un mot.
 */
export interface Maintenance {
  /**
   * L'état est-il CONNU, ou n'a-t-on simplement pas encore de réponse ?
   *
   * **Sans ce drapeau, la porte ne peut pas faire son travail** : `aucune` est
   * à la fois « rien de prévu » et « je ne sais pas encore », et confondre les
   * deux ferait figer son verdict avant d'avoir rien appris.
   */
  connu: boolean
  etat: 'aucune' | 'annoncee' | 'en_cours'
  motif: string | null
  debutA: string | null
  repriseA: string | null
  /** La reprise annoncée est passée et c'est toujours fermé — RG-9.11. */
  enRetard: boolean
  /** Rôle éditorial : la coque se peint normalement — RG-9.16. */
  laissezPasser: boolean
  /** Millisecondes avant la coupure annoncée. `null` hors annonce. */
  restantMs: number | null
  /**
   * Millisecondes avant la reprise annoncée. `null` quand on ne l'a pas.
   *
   * Calculée ICI et non dans l'écran : un `Date.now()` en plein rendu est une
   * lecture impure, et React l'interdit — la valeur changerait à chaque rendu
   * fortuit. L'horloge de ce crochet bat toutes les vingt secondes, et c'est
   * elle qui fait avancer le texte.
   */
  repriseDansMs: number | null
  /**
   * RG-9.8 — une épreuve notée ne se démarre pas ici.
   *
   * **Ce drapeau est prêt à être lu par `features/parcours/`**, qui décide du
   * démarrage d'un quiz de chapitre ou d'un examen final. Il vaut vrai pendant
   * la fenêtre, et déjà avant : commencer un examen de quatre-vingts minutes à
   * vingt minutes de la coupure, c'est le perdre.
   */
  epreuveInterdite: boolean
}

/**
 * Le rythme du sondage — mesuré en octets, pas en confort.
 *
 * La réponse pèse deux cents octets. Toutes les dix minutes hors annonce, c'est
 * un kilo-octet par heure sur un forfait compté ; toutes les vingt secondes
 * pendant la fenêtre, c'est ce qu'il faut pour que la reprise se voie sans
 * que le candidat ait à recharger — c'est-à-dire sans qu'il ait à se demander
 * si c'est revenu.
 */
function cadence(etat: Maintenance['etat'], restantMs: number | null): number {
  if (etat === 'en_cours') return 20_000
  if (restantMs !== null && restantMs < 3_600_000) return 30_000
  if (etat === 'annoncee') return 300_000

  return 600_000
}

export function useMaintenance(): Maintenance {
  /**
   * L'horloge locale, qui bat lentement.
   *
   * **Vingt secondes, pas une.** Le bandeau affiche des minutes : un rendu par
   * seconde ferait soixante fois le travail pour la même phrase, sur des
   * téléphones où chaque rendu se paie. 06 §6.3 refuse déjà le clignotement
   * pour la même famille de raisons.
   */
  const [maintenant, setMaintenant] = useState(() => Date.now())

  useEffect(() => {
    const battement = setInterval(() => {
      setMaintenant(Date.now())
    }, 20_000)

    return () => {
      clearInterval(battement)
    }
  }, [])

  // Le 503 aperçu au vol. Un état local plutôt qu'une lecture directe : la
  // variable de module ne déclenche aucun rendu à elle seule.
  const [vue, setVue] = useState(maintenanceObservee)

  useEffect(() => auSignalDeMaintenance(setVue), [])

  const { data, dataUpdatedAt } = useQuery({
    queryKey: ['maintenance'],
    queryFn: etatDeLaMaintenance,
    // La route est publique : une 401 n'existe pas, et un échec réseau ne doit
    // pas faire conclure à une maintenance — c'est peut-être le contraire.
    retry: false,
    staleTime: 10_000,
    refetchInterval: (requete) =>
      cadence(
        requete.state.data?.etat ?? 'aucune',
        restant(requete.state.data?.debut_a ?? null, Date.now()),
      ),
    // Le seul écran qui compte est celui d'un onglet qu'on rouvre après une
    // nuit : c'est là que la fenêtre a pu s'ouvrir sans qu'on la voie.
    refetchOnWindowFocus: true,
  })

  const serveur = data ?? null

  /**
   * **La source la plus RÉCENTE gagne, et c'est la seule règle qui tienne.**
   *
   * Le 503 aperçu peut dater d'une heure : la reprise a eu lieu, la route
   * publique répond « aucune », et croire le 503 afficherait une panne
   * inventée — pire que le remerciement que RG-9.14 refuse. À l'inverse, la
   * dernière réponse réussie peut dater d'avant la coupure : croire un
   * « aucune » périmé laisserait le candidat devant des écrans muets.
   */
  const observationPlusRecente = vue !== null && (serveur === null || vue.vueA > dataUpdatedAt)

  if (observationPlusRecente && vue !== null) {
    return {
      connu: true,
      etat: 'en_cours',
      motif: vue.motif,
      debutA: null,
      repriseA: vue.repriseA,
      enRetard: vue.enRetard,
      laissezPasser: false,
      restantMs: null,
      repriseDansMs: restant(vue.repriseA, maintenant),
      epreuveInterdite: true,
    }
  }

  if (serveur !== null && serveur.etat !== 'aucune') {
    return {
      connu: true,
      etat: serveur.etat,
      motif: serveur.motif,
      debutA: serveur.debut_a,
      repriseA: serveur.reprise_a,
      enRetard: serveur.en_retard,
      laissezPasser: serveur.laissez_passer,
      restantMs: restant(serveur.debut_a, maintenant),
      repriseDansMs: restant(serveur.reprise_a, maintenant),
      epreuveInterdite: interdite(serveur.etat, restant(serveur.debut_a, maintenant)),
    }
  }

  return {
    connu: serveur !== null,
    etat: 'aucune',
    motif: null,
    debutA: null,
    repriseA: null,
    enRetard: false,
    laissezPasser: serveur?.laissez_passer ?? false,
    restantMs: null,
    repriseDansMs: null,
    epreuveInterdite: false,
  }
}

function restant(instant: string | null, maintenant: number): number | null {
  if (instant === null) return null

  return new Date(instant).getTime() - maintenant
}

function interdite(etat: Maintenance['etat'], restantMs: number | null): boolean {
  if (etat === 'en_cours') return true
  if (etat !== 'annoncee' || restantMs === null) return false

  return restantMs < MINUTES_D_UNE_EPREUVE * 60_000
}
